import { Stack, Button, Box, CircularProgress } from "@mui/material";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getRecommendationsRequest,
  updateRecommendationsRequest,
  getScrollProductsRequest,
} from "services/modules/products";
import { GetScrollCategoriesRequest } from "services/modules/lookups/categories";
import { useThemePalette } from "common/hooks/theme_palette";
import FormIndex from "components/Atoms/SharedComponents/Forms/main";

export default function ProductRecommendations({ id }) {
  const { t } = useTranslation();
  const { oliveGreen } = useThemePalette();
  const dispatch = useDispatch();

  const recommendations = useSelector(
    (state) => state.products.recommendations,
  ); // Assuming this is where it's stored in reducer
  console.log(
    "🚀 ~ ProductRecommendations ~ recommendations:",
    recommendations,
  );
  const load = useSelector((state) => state.products.load);

  const products = useSelector((state) => state.products.filterProducts) || [];
  const productsCount = useSelector((state) => state.products.listCount);
  const productsLoading = useSelector((state) => state.products.load);

  const categories =
    useSelector((state) => state.categories.filterCategories) || [];
  const categoriesCount = useSelector((state) => state.categories.listCount);
  const categoriesLoading = useSelector((state) => state.categories.load);

  const formik = useFormik({
    initialValues: {
      recommended_products:
        recommendations?.recommended_products?.map((c) => ({
          label: c.name,
          value: c.id,
        })) || [],
      recommended_categories:
        recommendations?.recommended_categories?.map((c) => ({
          label: c.name,
          value: c.id,
        })) || [],
    },
    onSubmit: async (values) => {
      const payload = {
        recommended_products: values.recommended_products.map(
          (item) => item.value,
        ),
        recommended_categories: values.recommended_categories.map(
          (item) => item.value,
        ),
      };
      dispatch(updateRecommendationsRequest({ id, body: payload }));
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    if (id) {
      dispatch(getRecommendationsRequest({ id }));
    }
  }, [id, dispatch]);

  const fetchProducts = (filterObj) => {
    dispatch(
      getScrollProductsRequest({
        params: {
          page_size: 10,
          page_number: 1,
          ...filterObj,
        },
      }),
    );
  };

  const fetchCategories = (filterObj) => {
    dispatch(
      GetScrollCategoriesRequest({
        params: {
          page_size: 10,
          page_number: 1,
          hide_details: true,
          ...filterObj,
        },
      }),
    );
  };

  const structure = [
    {
      head: t("products.label_recommendations"),
      width: "100%",
      list: [
        {
          name: `recommended_products`,
          kind: "multiSelect",
          type: "text",
          label: t("products.label_recommended_products"),
          width: "100%",
          placeholder: t("products.placeholder_choose_products"),
          options: products,
          onOpen: products?.length <= 1 && fetchProducts,
          isLoading:
            productsLoading?.loading &&
            productsLoading?.type === "filter-products-list",
          getList: fetchProducts,
          applySearchFromRequest: true,
          count: productsCount,
        },
        {
          name: `recommended_categories`,
          kind: "multiSelect",
          type: "text",
          label: t("products.label_recommended_categories"),
          width: "100%",
          placeholder: t("products.placeholder_choose_categories"),
          options: categories,
          onOpen: categories?.length <= 1 && fetchCategories,
          isLoading: categoriesLoading?.loading,
          getList: fetchCategories,
          applySearchFromRequest: true,
          count: categoriesCount,
        },
      ],
    },
  ];

  return (
    <Stack sx={{ overflowY: "auto", height: 630, p: 2 }}>
      {load?.loading &&
        (load?.type === "recommendations-get" ||
          load?.type === "recommendations-update") && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              inset: 0,
              position: "absolute",
              zIndex: 1000,
              bgcolor: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(0.6px)",
            }}
          >
            <CircularProgress sx={{ color: oliveGreen }} />
          </Box>
        )}
      <FormIndex formStructures={structure} formik={formik}>
        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button
            variant="contained"
            type="submit"
            sx={{
              bgcolor: oliveGreen,
              color: "white",
              "&:hover": { bgcolor: oliveGreen },
            }}
          >
            {t("common.save")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              formik.setFieldValue("recommended_products", []);
              formik.setFieldValue("recommended_categories", []);
              // The user said: "and for clear { recommended_product_ids: [], recommended_category_ids: [] }"
              // I should probably also call the update API if they want to clear it on the server immediately,
              // but usually "Clear" button in forms just resets the local state.
              // However, the payload provided for clear suggests they might want to SEND the clear payload.
              // I'll stick to local reset for now, unless they click Save.
            }}
          >
            {t("common.clear")}
          </Button>
        </Stack>
      </FormIndex>
    </Stack>
  );
}
